import { IMAGE_CONSTRAINTS } from '../constants';

/**
 * Validate image file
 * @param {File} file - Image file to validate
 * @returns {object} Validation result with isValid and error message
 */
export const validateImageFile = (file) => {
  // Check file size
  if (file.size > IMAGE_CONSTRAINTS.MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File quá lớn. Kích thước tối đa: ${IMAGE_CONSTRAINTS.MAX_FILE_SIZE / 1024 / 1024}MB`
    };
  }

  // Check file type
  if (!IMAGE_CONSTRAINTS.ALLOWED_TYPES.includes(file.type)) {
    return {
      isValid: false,
      error: 'Định dạng file không được hỗ trợ. Chỉ chấp nhận: JPG, PNG, WebP'
    };
  }

  return { isValid: true };
};

/**
 * Generate unique filename for upload
 * @param {string} originalName - Original filename
 * @param {string} prefix - Filename prefix
 * @param {string} shipmentId - Shipment ID
 * @returns {string} Unique filename
 */
export const generateUniqueFileName = (originalName, prefix = 'img', shipmentId = null) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const fileExtension = originalName.split('.').pop().toLowerCase();
  const random = Math.random().toString(36).substring(2, 8);
  
  if (shipmentId) {
    return `${prefix}_${shipmentId}_${timestamp}_${random}.${fileExtension}`;
  }
  
  return `${prefix}_${timestamp}_${random}.${fileExtension}`;
};

/**
 * Compress and resize image
 * @param {File} file - Image file
 * @param {object} options - Compression options
 * @returns {Promise<string>} Base64 compressed image
 */
export const compressImage = (file, options = {}) => {
  const {
    maxWidth = IMAGE_CONSTRAINTS.MAX_WIDTH,
    maxHeight = IMAGE_CONSTRAINTS.MAX_HEIGHT,
    quality = IMAGE_CONSTRAINTS.COMPRESSION_QUALITY
  } = options;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = height * (maxWidth / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = width * (maxHeight / height);
            height = maxHeight;
          }
        }

        // Set canvas size
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const base64 = canvas.toDataURL('image/jpeg', quality);
        
        // Remove data URL prefix to get just the base64 string
        const base64Data = base64.split(',')[1];
        
        resolve(base64Data);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    
    // Create object URL for the image
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get image dimensions
 * @param {File} file - Image file
 * @returns {Promise<object>} Image dimensions
 */
export const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
      URL.revokeObjectURL(img.src);
    };
    
    img.onerror = () => {
      reject(new Error('Failed to get image dimensions'));
      URL.revokeObjectURL(img.src);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Create image preview URL
 * @param {File} file - Image file
 * @returns {string} Object URL for preview
 */
export const createImagePreview = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Clean up image preview URL
 * @param {string} url - Object URL to revoke
 */
export const cleanupImagePreview = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if device has camera
 * @returns {boolean} True if camera is available
 */
export const hasCameraSupport = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

/**
 * Capture image from camera
 * @param {object} options - Camera options
 * @returns {Promise<File>} Captured image file
 */
export const captureFromCamera = (options = {}) => {
  const {
    width = 1280,
    height = 720,
    facingMode = 'environment' // 'user' for front camera, 'environment' for back camera
  } = options;

  return new Promise((resolve, reject) => {
    if (!hasCameraSupport()) {
      reject(new Error('Camera not supported'));
      return;
    }

    navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: width },
        height: { ideal: height },
        facingMode: facingMode
      }
    })
    .then(stream => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.srcObject = stream;
      video.play();

      video.onloadedmetadata = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Capture frame
        ctx.drawImage(video, 0, 0);
        
        // Stop camera stream
        stream.getTracks().forEach(track => track.stop());
        
        // Convert to blob and then to file
        canvas.toBlob(blob => {
          const file = new File([blob], `camera_capture_${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          resolve(file);
        }, 'image/jpeg', 0.8);
      };
    })
    .catch(reject);
  });
};

/**
 * Extract EXIF orientation from image
 * @param {File} file - Image file
 * @returns {Promise<number>} EXIF orientation value
 */
export const getImageOrientation = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const view = new DataView(e.target.result);
      
      if (view.getUint16(0, false) !== 0xFFD8) {
        resolve(1); // Not a JPEG
        return;
      }
      
      const length = view.byteLength;
      let offset = 2;
      
      while (offset < length) {
        const marker = view.getUint16(offset, false);
        offset += 2;
        
        if (marker === 0xFFE1) {
          const little = view.getUint16(offset + 4, false) === 0x4949;
          offset += view.getUint16(offset, false);
          
          if (view.getUint32(offset, little) !== 0x002A) {
            resolve(1);
            return;
          }
          
          const firstIFDOffset = view.getUint32(offset + 4, little);
          const count = view.getUint16(offset + firstIFDOffset, little);
          
          for (let i = 0; i < count; i++) {
            const tagOffset = offset + firstIFDOffset + 2 + (i * 12);
            if (view.getUint16(tagOffset, little) === 0x0112) {
              resolve(view.getUint16(tagOffset + 8, little));
              return;
            }
          }
        }
        
        if ((marker & 0xFF00) !== 0xFF00) break;
        offset += view.getUint16(offset, false);
      }
      
      resolve(1);
    };
    
    reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
  });
};